import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  styled
} from '@mui/material';
import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Kpi } from '@oh-vcp/components-common/src/models/case/CaseDetails';
import { EconsultActionButton } from '@oh-vcp/components-ui';
import { useEconsultCaseDetailsContext } from '../econsultCaseDetailsContext/EconsultCaseDetailsContext';
import ValidatorUtils from '../econsultBaseValidator/EconsultBaseValidator';
import { ValidationTypes } from '../econsultBaseValidator/EconsultValidationTypes';
import { ValidationResult } from '../econsultBaseValidator/ValidationResult';

interface EconsultSendDialogProps {
  isOpen: boolean;
  onCancel: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onConfirm: (kpi: Kpi) => void;
}

const SendDialogSelect = styled(Select)({
  height: 30
});

const SendDialogInput = {
  height: 30,
  width: '100%'
};

const ErrorContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.error.light,
  borderColor: theme.palette.error.custom,
  borderStyle: 'solid',
  borderWidth: 1,
  borderRadius: 3,
  padding: 10,
  marginBottom: 10
}));

const vaildFieldMessageType = {
  display: 'none',
  fontSize: '0.75rem'
};
const inVaildFieldMessageType = {
  display: 'block',
  color: 'red',
  fontSize: '0.75rem'
};

const EconsultSendDialog: FC<EconsultSendDialogProps> = ({ isOpen, onCancel, onConfirm }) => {
  const { timeOptions } = useEconsultCaseDetailsContext();
  const { t } = useTranslation();
  const [timeSpentOnConsult, setTimeSpentOnConsult] = useState<string>('');
  const [minutesSpent, setMinutesSpent] = useState<string>('');
  const [comments, setComments] = useState<string>('');
  const [disableOk, setDisableOk] = useState<boolean>(false);
  const [disableCancel, setDisableCancel] = useState<boolean>(false);
  const [isErrorShown, setIsErrorShown] = useState<boolean>(false);
  const [minutesSpentErrorMessageStyle, setMinutesSpentErrorMessageStyle] =
    useState(vaildFieldMessageType);

  useEffect(() => {
    if (isOpen) {
      setTimeSpentOnConsult('');
      setMinutesSpent('');
      setComments('');
    }
  }, [isOpen]);

  const exactTimeRequired = timeOptions?.find(
    ({ label }) => label === timeSpentOnConsult
  )?.exactTimeRequired;

  const requiredFields: Map<string, ValidationTypes> = new Map();
  requiredFields.set('timeSpentOnConsult', ValidationTypes.Text);
  if (exactTimeRequired) {
    requiredFields.set('minutesSpent', ValidationTypes.Text);
    requiredFields.set('comments', ValidationTypes.Date);
  }
  const [dirtyFields, setFieldDiryty] = useState<string[]>([]);
  const [invalidFields, setFieldInvalid] = useState<string[]>([]);

  const handleInputChange = useCallback((setter: React.Dispatch<React.SetStateAction<string>>) => {
    return (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>
    ) => {
      setter(event.target.value as string);
    };
  }, []);
  const validateMinutesSpent = (minutesSpentIn: string) => {
    if (isErrorShown) {
      setIsErrorShown(false);
    }
    const fieldName = 'minutesSpent';
    const timeOption = timeOptions?.find(({ label }) => label === timeSpentOnConsult);

    const minutesSpentLocal = +minutesSpentIn;
    const minutesSpentValid =
      timeOption &&
      timeOption?.startRange &&
      timeOption?.endRange &&
      minutesSpentLocal >= timeOption?.startRange &&
      minutesSpentLocal <= timeOption?.endRange;

    if (minutesSpentValid) {
      setMinutesSpentErrorMessageStyle(vaildFieldMessageType);
      ValidatorUtils.removeFromList(invalidFields, fieldName);
      setFieldInvalid(invalidFields);
      return true;
    }
    setMinutesSpentErrorMessageStyle(inVaildFieldMessageType);
    ValidatorUtils.addToList(invalidFields, fieldName);
    ValidatorUtils.addToList(dirtyFields, fieldName);
    setFieldDiryty(dirtyFields);
    setFieldInvalid(invalidFields);
    return false;
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name;

    if (fieldName === 'minutesSpent') {
      validateMinutesSpent(event.target.value);
      setMinutesSpent(event.target.value);
      handleInputChange(setMinutesSpent);
    }
  };
  const handleConfirm = () => {
    const timeOption = timeOptions?.find(({ label }) => label === timeSpentOnConsult);
    const fieldValueMap = new Map<string, any>();
    fieldValueMap.set('timeSpentOnConsult', timeSpentOnConsult);
    if (exactTimeRequired) {
      fieldValueMap.set('minutesSpent', minutesSpent);
      fieldValueMap.set('comments', comments);
    }
    const validationResult: ValidationResult = ValidatorUtils.handleAllFieldsValidation(
      requiredFields,
      dirtyFields,
      invalidFields,
      fieldValueMap
    );
    setFieldDiryty(validationResult.dirtyFields);
    setFieldInvalid(validationResult.invalidFields);
    const isMunitesSpentValid = validateMinutesSpent(minutesSpent);
    if (validationResult.isValid && timeOption && (isMunitesSpentValid || !exactTimeRequired)) {
      setDisableOk(true);
      setDisableCancel(true);
      onConfirm({
        endRange: timeOption.endRange,
        startRange: timeOption.startRange,
        kpiQuestion: t('Case.consult.timeSpentOnConsult') as string,
        kpiAnswer: minutesSpent || null,
        kpiComment: comments
      });
      setIsErrorShown(false);
    } else {
      setIsErrorShown(true);
    }
  };
  const handleCancel = (event: React.MouseEvent<HTMLButtonElement>) => {
    setIsErrorShown(false);
    setMinutesSpentErrorMessageStyle(vaildFieldMessageType);
    dirtyFields.length = 0;
    setFieldDiryty(dirtyFields);
    invalidFields.length = 0;
    setFieldInvalid(invalidFields);
    onCancel(event);
  };
  return (
    <Dialog open={isOpen}>
      <DialogTitle>
        <Typography variant="h2" color="base.grey3">
          {t('Case.consult.beforeYouSend')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {isErrorShown && (
          <ErrorContainer>
            <Typography variant="body2" color="error.custom2">
              {t('Case.consult.validation.errors')}
            </Typography>
          </ErrorContainer>
        )}
        <Grid container>
          <Grid item xs={12} md={6}>
            <Typography>
              <Typography color="error.main" component="span" ml={-1.5} mr={0.5}>
                *
              </Typography>
              {t('Case.consult.timeSpentOnConsult')}:{' '}
            </Typography>
            {isErrorShown &&
              ValidatorUtils.isFieldNotValidAndDirty(
                dirtyFields,
                invalidFields,
                'timeSpentOnConsult'
              ) && (
                <Typography color="error.main" variant="body4">
                  {t('Case.patient.field.required')}
                </Typography>
              )}
          </Grid>
          <Grid item xs={12} md={6}>
            <SendDialogSelect
              name="timeSpentOnConsult"
              onChange={handleInputChange(setTimeSpentOnConsult)}
              value={timeSpentOnConsult}
              displayEmpty
              fullWidth>
              <MenuItem value="">{t('Case.consult.pleaseSelect')}</MenuItem>
              {timeOptions &&
                timeOptions
                  .filter(({ typeCd }) => typeCd === 'ORIGINAL')
                  .sort((a, b) => a.startRange - b.startRange)
                  .map(({ label }) => (
                    <MenuItem value={label} key={label}>
                      {label}
                    </MenuItem>
                  ))}
            </SendDialogSelect>
          </Grid>
          {exactTimeRequired && (
            <>
              <Grid item xs={12} md={6} mt={2}>
                <Typography>
                  <Typography color="error.main" component="span" ml={-1.5} mr={0.5}>
                    *
                  </Typography>
                  {t('Case.consult.minutesSpent')}:&nbsp;
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} mt={2}>
                <input
                  name="minutesSpent"
                  type="number"
                  min={26}
                  max={60}
                  value={minutesSpent}
                  onChange={handleChange}
                  style={SendDialogInput}
                />
              </Grid>
              <Typography id="minutesSpent" style={minutesSpentErrorMessageStyle}>
                {t('Case.consult.timeSpentOnConsult.invalid')}
              </Typography>
            </>
          )}
          <Grid item xs={12} mt={2}>
            {exactTimeRequired && (
              <Typography color="error.main" component="span" ml={-1.5} mr={0.5}>
                *
              </Typography>
            )}

            <TextField
              value={comments}
              name="comments"
              onChange={handleInputChange(setComments)}
              multiline
              rows={5}
              fullWidth
              placeholder={t('Case.consult.enterComments') as string}
              inputProps={{ maxLength: 4000 }}
            />
          </Grid>
          <Grid container item xs={12} justifyContent="flex-end" pt={3}>
            <EconsultActionButton
              variant="contained"
              color="primary"
              sx={{ mr: 1 }}
              onClick={handleConfirm}
              disabled={disableOk}>
              {t('Case.button.ok')}
            </EconsultActionButton>
            <EconsultActionButton
              variant="contained"
              color="primary"
              onClick={handleCancel}
              disabled={disableCancel}>
              {t('Case.button.cancel')}
            </EconsultActionButton>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default EconsultSendDialog;
